/**
 * TeamScoreboard.tsx — AEs ranked by attainment.
 * Wired to live Supabase data.
 *
 * Route: /reporting/team
 *
 * Columns: Name · Manager · Quota · Forecast · Attainment % · # Stalled · Rank
 *  - Quota: sales_plans.quota_annual (current fiscal year)
 *  - Forecast: sum of v_account_forecast.weighted_value for AE
 *  - Attainment %: v_ae_pipeline_summary.won_value / quota_annual
 *  - # Stalled: accounts owned by AE with last activity > 14d
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  AccountRow,
  ForecastRow,
  PipelineSummaryRow,
  SalesPlanRow,
  UserRow,
  daysSince,
  fmtUSD,
  listAccountForecasts,
  listAccounts,
  listAllUsers,
  listCpStatusFor,
  listPipelineSummary,
  listSalesPlans,
  pctOf,
  summarizeCpStatus,
} from "@/lib/reporting-live";
import { friendlyError } from "@/lib/friendly-error";

type SortField = "name" | "quota" | "forecast" | "attainment" | "stalled";

interface RowVM {
  ae: UserRow;
  managerName: string;
  quota: number;
  forecast: number;
  won: number;
  attainmentPct: number;
  stalled: number;
}

export default function TeamScoreboard() {
  const [, navigate] = useLocation();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [pipeline, setPipeline] = useState<PipelineSummaryRow[]>([]);
  const [plans, setPlans] = useState<SalesPlanRow[]>([]);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [forecasts, setForecasts] = useState<ForecastRow[]>([]);
  const [cpByAccount, setCpByAccount] =
    useState<ReturnType<typeof summarizeCpStatus> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("attainment");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [us, ps, pl, ac, fc] = await Promise.all([
          listAllUsers(),
          listPipelineSummary(),
          listSalesPlans(),
          listAccounts(),
          listAccountForecasts(),
        ]);
        if (cancelled) return;
        setUsers(us);
        setPipeline(ps);
        setPlans(pl);
        setAccounts(ac);
        setForecasts(fc);
        const ids = ac.map((a) => a.id);
        const cp = ids.length > 0 ? await listCpStatusFor(ids) : [];
        if (cancelled) return;
        setCpByAccount(summarizeCpStatus(cp));
        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;
        setError(friendlyError(err));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

  const rows: RowVM[] = useMemo(() => {
    const aes = users.filter((u) => u.role === "ae");

    const planByAe = new Map<number, number>();
    plans.forEach((p) => planByAe.set(p.ae_user_id, Number(p.quota_annual || 0)));

    const pipelineByAe = new Map<number, PipelineSummaryRow>();
    pipeline.forEach((p) => pipelineByAe.set(p.ae_user_id, p));

    const forecastByAe = new Map<number, number>();
    forecasts.forEach((f) => {
      forecastByAe.set(
        f.ae_user_id,
        (forecastByAe.get(f.ae_user_id) || 0) + Number(f.weighted_value || 0),
      );
    });

    const stalledByAe = new Map<number, number>();
    accounts.forEach((a) => {
      const cp = cpByAccount?.get(a.id);
      const last = cp?.lastUpdated || a.updated_at;
      if (daysSince(last) > 14) {
        stalledByAe.set(a.owner_user_id, (stalledByAe.get(a.owner_user_id) || 0) + 1);
      }
    });

    return aes.map((ae) => {
      const ps = pipelineByAe.get(ae.id);
      const quota = planByAe.get(ae.id) || Number(ps?.quota_annual || 0);
      const forecast = forecastByAe.get(ae.id) || 0;
      const won = Number(ps?.won_value || 0);
      const attainmentPct = quota > 0 ? Math.round((won / quota) * 100) : 0;
      const managerName = ae.manager_user_id
        ? userMap.get(ae.manager_user_id)?.name || "—"
        : "—";
      return {
        ae,
        managerName,
        quota,
        forecast,
        won,
        attainmentPct,
        stalled: stalledByAe.get(ae.id) || 0,
      };
    });
  }, [users, plans, pipeline, forecasts, accounts, cpByAccount, userMap]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let va: number | string;
      let vb: number | string;
      switch (sortField) {
        case "name":
          va = a.ae.name || a.ae.email;
          vb = b.ae.name || b.ae.email;
          break;
        case "quota":
          va = a.quota;
          vb = b.quota;
          break;
        case "forecast":
          va = a.forecast;
          vb = b.forecast;
          break;
        case "stalled":
          va = a.stalled;
          vb = b.stalled;
          break;
        case "attainment":
        default:
          va = a.attainmentPct;
          vb = b.attainmentPct;
          break;
      }
      const cmp = typeof va === "string" ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortField, sortDir]);

  function clickHeader(f: SortField) {
    if (sortField === f) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortField(f);
      setSortDir(f === "name" ? "asc" : "desc");
    }
  }

  const arrow = (f: SortField) =>
    sortField === f ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  return (
    <OperatorShell breadcrumbs={[{ label: "Reporting", href: "/reporting" }, { label: "Team Scoreboard" }]}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <a
          href="/reporting"
          style={{ fontSize: 12, color: "#6B6B70", textDecoration: "none", fontWeight: 500 }}
        >
          ← Reporting Hub
        </a>
        <h1 style={{ fontSize: 24, fontWeight: 900, color: "#0B0B0F", margin: "10px 0 4px" }}>
          Team Scoreboard
        </h1>
        <div style={{ fontSize: 13, color: "#6B6B70", marginBottom: 20 }}>
          AEs sorted by attainment. Click a row to drill into her truth screen.
        </div>

        {error && (
          <div
            style={{
              padding: 12,
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              color: "#991B1B",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#FAFAF7" }}>
                {[
                  { f: "name" as const, label: "AE" },
                  { f: null, label: "Manager *" },
                  { f: "quota" as const, label: "Quota" },
                  { f: "forecast" as const, label: "Weighted forecast" },
                  { f: "attainment" as const, label: "Attainment" },
                  { f: "stalled" as const, label: "Stalled" },
                  { f: null, label: "Rank" },
                ].map((h, i) => (
                  <th
                    key={h.label}
                    onClick={() => h.f && clickHeader(h.f)}
                    style={{
                      padding: "10px 14px",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "#8E8E94",
                      borderBottom: "1px solid #EFEFEE",
                      cursor: h.f ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    {h.label}
                    {h.f && arrow(h.f)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: 20, color: "#8E8E94", textAlign: "center" }}>
                    Loading…
                  </td>
                </tr>
              ) : sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 20, color: "#8E8E94", textAlign: "center" }}>
                    No AEs in this tenant.
                  </td>
                </tr>
              ) : (
                sortedRows.map((r, i) => (
                  <tr
                    key={r.ae.id}
                    onClick={() => navigate(`/reporting/rep/${r.ae.id}`)}
                    style={{ cursor: "pointer", borderTop: "1px solid #FAFAF7" }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLElement).style.background = "#FAFAF7")
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLElement).style.background = "#FFFFFF")
                    }
                  >
                    <td style={{ padding: "10px 14px", color: "#0B0B0F", fontWeight: 600 }}>
                      {r.ae.name || r.ae.email}
                      <div style={{ fontSize: 11, color: "#8E8E94", fontWeight: 400 }}>
                        {r.ae.email}
                      </div>
                    </td>
                    <td style={{ padding: "10px 14px", color: "#C9C9CC", fontStyle: "italic", fontSize: 11 }}>coming soon</td>
                    <td style={{ padding: "10px 14px", color: "#0B0B0F" }}>
                      {r.quota > 0 ? fmtUSD(r.quota) : "—"}
                    </td>
                    <td style={{ padding: "10px 14px", color: "#0B0B0F" }}>
                      {fmtUSD(r.forecast)}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {r.quota > 0 ? (
                        <span
                          style={{
                            color:
                              r.attainmentPct >= 80
                                ? "#16A34A"
                                : r.attainmentPct >= 50
                                  ? "#CA8A04"
                                  : "#DC2626",
                            fontWeight: 800,
                          }}
                        >
                          {r.attainmentPct}%
                        </span>
                      ) : (
                        <span style={{ color: "#C9C9CC" }}>no quota</span>
                      )}
                      <div style={{ fontSize: 11, color: "#8E8E94" }}>
                        {fmtUSD(r.won)} won
                      </div>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ color: r.stalled > 0 ? "#DC2626" : "#525258", fontWeight: 600 }}>
                        {r.stalled}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", color: "#8E8E94" }}>#{i + 1}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: "#8E8E94" }}>
          Quota from sales_plans · forecast and won from v_ae_pipeline_summary &amp; v_account_forecast ·
          stalled = accounts with no CP or status update for &gt; 14 days.
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: "#C9C9CC" }}>
          * Manager hierarchy reporting — coming soon. We're building this out.
        </div>
      </div>
    </OperatorShell>
  );
}
